import { useMemo } from 'react';
import { useFormattedDate } from './text/useFormattedDate';
import { useFuelTypesLookup } from './text/useFuelTypesLookup';
import { useAvailableFromDate } from './text/useAvailableFrom';
import { useVehicleInspectionDueDate } from './text/useVehicleInspectionDueDate';
import { useServicesContext } from '../context';
import { useI18n } from '@oneaudi/i18n-context';
import { HighlightTechdataKeys, warrantyCodeLookup } from '@oneaudi/vtp-configuration-service';
import { messages } from '../i18n/messages';
export const useDescription = (iconLabel, techdataKey = '', vehicleData, vehicleDetail) => {
    var _a;
    const availableFromDateLabels = useAvailableFromDate(vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.availableFromDate, vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.availableFromCode, vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.availableFrom);
    const vehicleInspectionDueDateDescription = useVehicleInspectionDueDate(vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.generalinspectionDueDate);
    const initialRegistrationDateLabels = useFormattedDate((_a = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.used) === null || _a === void 0 ? void 0 : _a.initialRegistrationDate);
    const generalInspectionDateLabels = useFormattedDate(vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.generalInspection);
    const generalInspectionString = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.generalInspectionString;
    const fuelData = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.fuel;
    const fuelCode = fuelData === null || fuelData === void 0 ? void 0 : fuelData.code;
    const fuelDescription = fuelData === null || fuelData === void 0 ? void 0 : fuelData.description;
    const fuelCodeString = useI18n({
        id: `nemo.ui.sc.eec.fuel.${fuelCode === null || fuelCode === void 0 ? void 0 : fuelCode.toLowerCase()}`,
        defaultMessage: `${fuelDescription}`,
    });
    const fuelTypesLookup = useFuelTypesLookup();
    const vehiclePreuse = (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.preuse) || 'none';
    const vehiclePreuseDescription = useI18n(messages.vehiclePreuseDescription[vehiclePreuse]);
    const soonMileage = useI18n(messages.availableSoonMileage);
    const usedCarMileage = useI18n(messages.usedCarMileage);
    const warrantyExpiryDateDescription = useFormattedDate(vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.warrantyExpiryDate);
    const convertStringDateToNumber = (dateInStringFormat) => new Date(dateInStringFormat).getTime();
    const availableFromDateForSpecialCase = useFormattedDate(convertStringDateToNumber(vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.availableFrom));
    const availableFromDateFallbackLabel = useI18n(messages.availableSoon);
    const { getConfiguration } = useServicesContext();
    const { scopes } = getConfiguration();
    const hideConcreteAvailableFromDate = scopes === null || scopes === void 0 ? void 0 : scopes.hideConcreteAvailableSoonDate;
    const availableFrom = useFormattedDate(convertStringDateToNumber(vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.availableFrom));
    const checkScopeForAvailableSoon = hideConcreteAvailableFromDate
        ? availableFromDateFallbackLabel
        : availableFrom;
    const availableFromCode = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.availableFromCode;
    const genericLabel = useI18n({
        id: `nemo.ui.sc.highlight-label.${techdataKey.toLowerCase()}`,
        defaultMessage: iconLabel,
    });
    const warrantyPlus = useI18n({
        id: 'stockcars.warrantyplus.label',
        defaultMessage: `<span class='nm-brand-plus' style='color: #f50537;'>:plus</span>`,
    });
    return useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18;
        let techdataValue;
        let itemLabel = genericLabel;
        if (vehicleData) {
            switch (true) {
                case techdataKey.includes('ElectricRange'):
                    if ((_a = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.io) === null || _a === void 0 ? void 0 : _a.fuels) {
                        const electricEngine = (_b = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.io) === null || _b === void 0 ? void 0 : _b.fuels.find((fuelType) => fuelType.fuel === 'ELECTRICAL');
                        if (electricEngine) {
                            const { value, unit } = ((_c = electricEngine === null || electricEngine === void 0 ? void 0 : electricEngine.range) === null || _c === void 0 ? void 0 : _c.consolidated) || {};
                            if ((_d = electricEngine === null || electricEngine === void 0 ? void 0 : electricEngine.range) === null || _d === void 0 ? void 0 : _d.consolidated) {
                                techdataValue = `${value} ${unit}`;
                            }
                        }
                    }
                    break;
                case techdataKey.includes('FuelType'):
                    if (fuelData) {
                        techdataValue = `${fuelCodeString}`;
                    }
                    else if (((_e = vehicleData.io) === null || _e === void 0 ? void 0 : _e.fuels) && ((_f = vehicleData.io) === null || _f === void 0 ? void 0 : _f.fuels[1])) {
                        techdataValue = `${fuelTypesLookup[(_h = (_g = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.io) === null || _g === void 0 ? void 0 : _g.fuels[0]) === null || _h === void 0 ? void 0 : _h.fuel] || ((_k = (_j = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.io) === null || _j === void 0 ? void 0 : _j.fuels[0]) === null || _k === void 0 ? void 0 : _k.fuel)} / ${fuelTypesLookup[(_m = (_l = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.io) === null || _l === void 0 ? void 0 : _l.fuels[1]) === null || _m === void 0 ? void 0 : _m.fuel] || ((_p = (_o = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.io) === null || _o === void 0 ? void 0 : _o.fuels[1]) === null || _p === void 0 ? void 0 : _p.fuel)}`;
                    }
                    else if ((_q = vehicleData.io) === null || _q === void 0 ? void 0 : _q.fuels) {
                        techdataValue =
                            fuelTypesLookup[(_s = (_r = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.io) === null || _r === void 0 ? void 0 : _r.fuels[0]) === null || _s === void 0 ? void 0 : _s.fuel] || ((_u = (_t = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.io) === null || _t === void 0 ? void 0 : _t.fuels[0]) === null || _u === void 0 ? void 0 : _u.fuel);
                    }
                    break;
                case techdataKey.includes('EnvironmentTax'):
                    if (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.typedPrices) {
                        const type = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.typedPrices.find((typedPricesType) => typedPricesType.type === 'environment_tax');
                        if (type) {
                            const formattedValue = type.formatted;
                            techdataValue = formattedValue;
                        }
                    }
                    break;
                case techdataKey.includes('PowerDisplay'):
                    if (vehicleData.powerDisplay) {
                        techdataValue = vehicleData.powerDisplay;
                    }
                    break;
                case techdataKey.includes('RegistrationPlate'):
                    if ((_v = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.used) === null || _v === void 0 ? void 0 : _v.numberplate) {
                        techdataValue = (_w = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.used) === null || _w === void 0 ? void 0 : _w.numberplate;
                    }
                    break;
                case techdataKey.includes('electrical-power'):
                    if (vehicleDetail === null || vehicleDetail === void 0 ? void 0 : vehicleDetail.techData.groups) {
                        techdataValue = vehicleData.powerDisplay;
                    }
                    break;
                case techdataKey.includes('Mileage'):
                    const specialCaseForMileageLabel = ((_x = vehicleData.dealer) === null || _x === void 0 ? void 0 : _x.id) === '03611' && vehicleData.availableFromCode === 'date';
                    const mileageLabel = specialCaseForMileageLabel
                        ? usedCarMileage
                        : soonMileage || iconLabel;
                    if ((_y = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.used) === null || _y === void 0 ? void 0 : _y.mileageFormatted) {
                        techdataValue = `${(_z = vehicleData.used) === null || _z === void 0 ? void 0 : _z.mileageFormatted} ${(_0 = vehicleData.used) === null || _0 === void 0 ? void 0 : _0.mileageUnit}`;
                        itemLabel = mileageLabel;
                    }
                    break;
                case techdataKey.includes('DriveType'):
                    if ((_1 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.driveType) === null || _1 === void 0 ? void 0 : _1.description) {
                        techdataValue = (_2 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.driveType) === null || _2 === void 0 ? void 0 : _2.description;
                    }
                    break;
                case techdataKey.includes('GearType'):
                    if ((_3 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.gearType) === null || _3 === void 0 ? void 0 : _3.description) {
                        techdataValue = (_4 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.gearType) === null || _4 === void 0 ? void 0 : _4.description;
                    }
                    break;
                case techdataKey.includes('ExtColor'):
                    if ((_5 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.extColor) === null || _5 === void 0 ? void 0 : _5.description) {
                        techdataValue = (_6 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.extColor) === null || _6 === void 0 ? void 0 : _6.description;
                    }
                    break;
                case techdataKey.includes('InitialRegistrationDate'):
                    techdataValue = initialRegistrationDateLabels;
                    break;
                case techdataKey.includes('IntColor'):
                    if ((_7 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.intColor) === null || _7 === void 0 ? void 0 : _7.description) {
                        techdataValue = (_8 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.intColor) === null || _8 === void 0 ? void 0 : _8.description;
                    }
                    break;
                case techdataKey.includes('UpholsteryColor'):
                    if ((_9 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.upholsteryColor) === null || _9 === void 0 ? void 0 : _9.description) {
                        techdataValue = (_10 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.upholsteryColor) === null || _10 === void 0 ? void 0 : _10.description;
                    }
                    break;
                case techdataKey.includes('UpholsteryType'):
                    if ((_11 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.upholsteryType) === null || _11 === void 0 ? void 0 : _11.description) {
                        techdataValue = (_12 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.upholsteryType) === null || _12 === void 0 ? void 0 : _12.description;
                    }
                    break;
                case techdataKey.includes('ProductionYear'):
                    if (vehicleData.productionYear) {
                        techdataValue = vehicleData.productionYear;
                    }
                    break;
                case techdataKey.includes('ModelYear'):
                    if (vehicleData.modelYear) {
                        techdataValue = String(vehicleData.modelYear);
                    }
                    break;
                case techdataKey.includes('SalesModelYear'):
                    if (vehicleData.salesModelYear) {
                        techdataValue = String(vehicleData.salesModelYear);
                    }
                    break;
                case techdataKey.includes('Acceleration'):
                    if (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.acceleration) {
                        techdataValue = String(vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.acceleration);
                    }
                    break;
                case techdataKey.includes('TopSpeed'):
                    if (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.topSpeed) {
                        techdataValue = String(vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.topSpeed);
                    }
                    break;
                case techdataKey.includes('GearBox'):
                    if ((_13 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.gearBox) === null || _13 === void 0 ? void 0 : _13.description) {
                        techdataValue = (_14 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.gearBox) === null || _14 === void 0 ? void 0 : _14.description;
                    }
                    break;
                case techdataKey.includes('NumPreviousOwners'):
                    if ((_15 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.used) === null || _15 === void 0 ? void 0 : _15.numPreviousOwners) {
                        techdataValue = String((_16 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.used) === null || _16 === void 0 ? void 0 : _16.numPreviousOwners);
                    }
                    break;
                case techdataKey.includes('AvailableFromDate'):
                    const [label, description] = availableFromDateLabels;
                    if (label && description) {
                        itemLabel = label;
                        techdataValue = description;
                    }
                    if (availableFromCode === 'date') {
                        if (((_17 = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.dealer) === null || _17 === void 0 ? void 0 : _17.id) === '03611') {
                            techdataValue = availableFromDateForSpecialCase;
                        }
                        else {
                            techdataValue = checkScopeForAvailableSoon;
                        }
                    }
                    break;
                case techdataKey.includes('VehicleInspectionDueDate'):
                    if (vehicleInspectionDueDateDescription) {
                        techdataValue = vehicleInspectionDueDateDescription;
                    }
                    break;
                case techdataKey.includes('GeneralInspection'):
                    if (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.generalInspection) {
                        techdataValue = generalInspectionDateLabels;
                    }
                    else if (generalInspectionString) {
                        techdataValue = generalInspectionString;
                    }
                    break;
                case techdataKey.includes('VehiclePreuse'):
                    if (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.preuse) {
                        techdataValue = vehiclePreuseDescription;
                    }
                    break;
                case techdataKey.includes(HighlightTechdataKeys.PREUSE_DETAIL):
                    techdataValue = (_18 = vehicleDetail === null || vehicleDetail === void 0 ? void 0 : vehicleDetail.preusedDetail) === null || _18 === void 0 ? void 0 : _18.description;
                    break;
                case techdataKey.includes('RecycleFee') && !techdataKey.includes('RecycleFeeType'):
                    if (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.recycleFee) {
                        const recycleFee = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.recycleFee.find((recycleFeeItem) => recycleFeeItem.code === 'amount');
                        if (recycleFee && recycleFee.description) {
                            const recycleFeeDescription = recycleFee.description;
                            techdataValue = recycleFeeDescription;
                        }
                    }
                    break;
                case techdataKey.includes('RecycleFeeType'):
                    if (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.recycleFee) {
                        const recycleFeeType = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.recycleFee.find((recycleFeeItem) => recycleFeeItem.code === 'type');
                        if (recycleFeeType && recycleFeeType.description) {
                            const recycleFeeTypeDescription = recycleFeeType.description;
                            techdataValue = recycleFeeTypeDescription;
                        }
                    }
                    break;
                case techdataKey.includes('WarrantyExpiryDate'):
                    if (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.warrantyExpiryDate) {
                        techdataValue = warrantyExpiryDateDescription;
                    }
                    break;
                case techdataKey.includes('WarrantyTypePlus'):
                    if (vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.warrantyPlus) {
                        techdataValue = warrantyPlus;
                    }
                    break;
                case Object.keys(warrantyCodeLookup).includes(techdataKey):
                    break;
                default:
                    console.debug(`invalid Highlight techdatakey: '${techdataKey}'`);
                    techdataValue = undefined;
            }
        }
        return [itemLabel, techdataValue];
    }, [
        availableFromDateLabels,
        fuelTypesLookup,
        generalInspectionDateLabels,
        generalInspectionString,
        iconLabel,
        initialRegistrationDateLabels,
        techdataKey,
        vehicleData,
        vehicleDetail,
        vehiclePreuseDescription,
        vehicleInspectionDueDateDescription,
        warrantyExpiryDateDescription,
    ]);
};
